import { useSwrFetch } from "./useSwrFetch";

// Matches the endpoint's own cache lifetime, so a view left open when the daily
// sync lands picks the new rate up on its next poll rather than on a hard
// reload. Without it nothing revalidates at all: focus and reconnect
// revalidation are deliberately off, which leaves a mounted view on the rate it
// first fetched for as long as it stays mounted.
const EXCHANGE_RATE_REFRESH_INTERVAL_MS = 300_000;

interface UseExchangeRateProps {
  baseCurrencyCode: string | undefined;
  quoteCurrencyCode: string | undefined;
}

interface UseExchangeRateResult {
  exchangeRate: ExchangeRate | null;
  loading: boolean;
}

/**
 * The latest stored rate for one ordered currency pair.
 *
 * `rate` is units of `quote_currency_code` per 1 unit of `base_currency_code`,
 * so a caller always multiplies — the server resolves direction, including the
 * one it does not store.
 *
 * The URL is null until both codes are known and differ, which is how SWR skips
 * a request that cannot say anything. The SWR key is the URL, so every
 * component asking for the same pair shares one request and one cache entry for
 * the session; call this once per direction from a list or table container
 * rather than per row. Revalidation on focus and reconnect stays off — a rate
 * that changes once a business day should not refetch because a tab regained
 * focus — so the slow poll above is what carries a newly synced rate into a view
 * that is already open.
 */
export function useExchangeRate(
  props: UseExchangeRateProps,
): UseExchangeRateResult {
  const { baseCurrencyCode, quoteCurrencyCode } = props;

  const url =
    baseCurrencyCode &&
    quoteCurrencyCode &&
    baseCurrencyCode !== quoteCurrencyCode
      ? `/exchange-rates?base=${baseCurrencyCode}&quote=${quoteCurrencyCode}`
      : null;

  const { data, loading } = useSwrFetch<ExchangeRate>({
    url,
    errorMessage: "Failed to fetch exchange rate",
    swrOptions: { refreshInterval: EXCHANGE_RATE_REFRESH_INTERVAL_MS },
  });

  // An empty registry answers 200 with a null rate, and a failed request leaves
  // no data at all. Both mean the same thing to a caller: show nothing.
  return {
    exchangeRate: data?.rate != null ? data : null,
    loading,
  };
}
